import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { LoadingSpinner } from '@/components/common';
// Only the error PARSERS -- never the authenticated axios instance. A visitor
// here has no session and must never send one (same rule as PublicQuoteJourney).
import { NON_FIELD_KEY, getErrorMessage, getFieldErrors } from '@/services/api';
import { DUPLICATE_FALLBACK, attemptKeyFor, isStartResponse, quotePagePath, type StartResponse } from './StartNotes';
import { quoteBasePath } from './PublicQuoteChooser';

/**
 * Health and Travel quotes from a broker's own page (owner, 24 Sep: "Motor,
 * Health and Travel").
 *
 * ROUTES: `/public/quote/:brokerSlug/health` and `/travel`, reached from the
 * broker's chooser (`PublicQuoteChooser`), their microsite tiles, or a line
 * page's "Get a quote". Always slugged: the platform's own site does not quote.
 *
 * ONE ENDPOINT PER LINE: `POST /policy/api/public/quotes/start/<line>/?site=<slug>`
 * (`apps/quotes/public_origination_lines.py`). It writes the lead and the quote
 * request under the broker the slug names and answers with the quote's own
 * token -- the SAME response shape as the motor start -- and the visitor goes
 * to the SAME final page as motor, `/public/quotes/<token>`. No insurer prices
 * these lines online yet, so that page says the advisor is preparing quotes; it
 * re-reads itself, so the broker's options appear there as they are added.
 *
 * NOTHING IS PRICED OR ESTIMATED ON THIS PAGE. Every premium shown is a
 * persisted quote the server sent.
 */

const API_ROOT = '/policy/api/public';

/** Health/travel never call an insurer inside the request today, but a live
 *  adapter would, so the wait mirrors the motor start's budget. */
const START_TIMEOUT_MS = 115_000;

export type NonMotorLine = 'health' | 'travel';

export const SUM_INSURED_OPTIONS: { value: number; label: string }[] = [
  { value: 300000, label: 'Rs 3 lakh' },
  { value: 500000, label: 'Rs 5 lakh' },
  { value: 1000000, label: 'Rs 10 lakh' },
  { value: 1500000, label: 'Rs 15 lakh' },
  { value: 2500000, label: 'Rs 25 lakh' },
  { value: 5000000, label: 'Rs 50 lakh' },
  { value: 10000000, label: 'Rs 1 crore' },
];

export const TRAVEL_REGIONS: { value: string; label: string }[] = [
  { value: 'worldwide_incl_us_canada', label: 'Worldwide including USA / Canada' },
  { value: 'worldwide_excl_us_canada', label: 'Worldwide excluding USA / Canada' },
  { value: 'schengen_europe', label: 'Schengen / Europe' },
  { value: 'asia', label: 'Asia' },
  { value: 'asia_excl_japan', label: 'Asia excluding Japan' },
  { value: 'africa_middle_east', label: 'Africa / Middle East' },
  { value: 'other', label: 'Other' },
];

const TRAVEL_PURPOSES = [
  { value: 'leisure', label: 'Leisure / holiday' },
  { value: 'business', label: 'Business' },
  { value: 'student', label: 'Study abroad' },
];

/** The fields the server can refuse that this page draws an error beside. */
const HEALTH_FIELDS = new Set([
  'name', 'phone', 'email', 'pincode', 'members', 'sum_insured',
  'pre_existing_condition', 'existing_insurer_name', 'existing_policy_expiry',
]);
const TRAVEL_FIELDS = new Set([
  'name', 'phone', 'email', 'pincode', 'trip_type', 'region', 'countries',
  'start_date', 'end_date', 'traveller_ages', 'purpose',
]);

type Stage = 'form' | 'done' | 'uncertain' | 'already';

/** The start may have reached the server and simply not come back. */
function mayHaveReachedServer(err: unknown): boolean {
  const e = err as { response?: { status?: number } } | null;
  if (!e) return false;
  const status = e.response?.status;
  if (status === undefined) return true;
  return status === 502 || status === 503 || status === 504;
}

const inputClass =
  'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-red-600 dark:text-red-400">{message}</p>;
}

function Panel({ tone, title, children }: { tone: 'neutral' | 'good' | 'warn'; title: string; children: React.ReactNode }) {
  const tones = {
    neutral: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white',
    good: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-200',
    warn: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200',
  } as const;
  return (
    <section className={`mb-6 rounded-xl border p-6 shadow-sm ${tones[tone]}`}>
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="mt-2 text-sm">{children}</div>
    </section>
  );
}

function AgeInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <input
        id={id}
        inputMode="numeric"
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 3))}
        required
      />
    </div>
  );
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function PublicLineQuoteJourney({ line }: { line: NonMotorLine }) {
  const { brokerSlug = '' } = useParams<{ brokerSlug: string }>();

  const [stage, setStage] = useState<Stage>('form');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<StartResponse | null>(null);
  const [alreadyDetail, setAlreadyDetail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [companyWebsite, setCompanyWebsite] = useState('');

  const [contact, setContact] = useState({ name: '', phone: '', email: '', pincode: '' });

  // --- health ---
  const [cover, setCover] = useState({ self: true, spouse: false, father: false, mother: false });
  const [childCount, setChildCount] = useState(0);
  const [ages, setAges] = useState<Record<string, string>>({});
  const [sumInsured, setSumInsured] = useState('500000');
  const [preExisting, setPreExisting] = useState<'no' | 'yes'>('no');
  const [showExisting, setShowExisting] = useState(false);
  const [existing, setExisting] = useState({ insurer: '', expiry: '' });

  // --- travel ---
  const [tripType, setTripType] = useState<'single' | 'annual_multi_trip'>('single');
  const [region, setRegion] = useState('worldwide_excl_us_canada');
  const [countries, setCountries] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [travellerCount, setTravellerCount] = useState(1);
  const [travellerAges, setTravellerAges] = useState<string[]>(['']);
  const [purpose, setPurpose] = useState('leisure');

  const title = line === 'health' ? 'Health insurance quote' : 'Travel insurance quote';

  /** Who the health plan covers, in the order the server stores them. */
  const healthMembers = (): { key: string; relation: string; label: string }[] => {
    const rows: { key: string; relation: string; label: string }[] = [];
    if (cover.self) rows.push({ key: 'self', relation: 'self', label: 'Your age' });
    if (cover.spouse) rows.push({ key: 'spouse', relation: 'spouse', label: "Spouse's age" });
    for (let i = 1; i <= childCount; i += 1) {
      rows.push({ key: `child${i}`, relation: 'child', label: `Child ${i} age` });
    }
    if (cover.father) rows.push({ key: 'father', relation: 'father', label: "Father's age" });
    if (cover.mother) rows.push({ key: 'mother', relation: 'mother', label: "Mother's age" });
    return rows;
  };

  const setTravellers = (count: number) => {
    setTravellerCount(count);
    setTravellerAges((prev) => Array.from({ length: count }, (_, i) => prev[i] ?? ''));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const base = {
      site: brokerSlug,
      company_website: companyWebsite,
      // Only THIS attempt may be handed back a request it already started.
      attempt_key: attemptKeyFor(`${line}:${brokerSlug}`),
      name: contact.name.trim(),
      phone: contact.phone.trim(),
      email: contact.email.trim(),
      pincode: contact.pincode.trim(),
    };
    let body: Record<string, unknown>;
    if (line === 'health') {
      const members = healthMembers();
      if (members.length === 0) {
        setFieldErrors({ members: 'Choose at least one person to cover.' });
        return;
      }
      body = {
        ...base,
        members: members.map((m) => ({ relation: m.relation, age: Number(ages[m.key] ?? '') })),
        sum_insured: Number(sumInsured),
        pre_existing_condition: preExisting === 'yes',
        existing_insurer_name: showExisting ? existing.insurer.trim() : '',
        existing_policy_expiry: (showExisting && existing.expiry) || null,
      };
    } else {
      body = {
        ...base,
        trip_type: tripType,
        region,
        countries: countries.trim(),
        start_date: startDate || null,
        // An annual cover's end is the server's to derive (a year from start).
        end_date: tripType === 'annual_multi_trip' ? null : endDate || null,
        traveller_ages: travellerAges.map((a) => Number(a)),
        purpose,
      };
    }

    setSubmitting(true);
    try {
      const res = await axios.post<StartResponse>(
        `${API_ROOT}/quotes/start/${line}/${brokerSlug ? `?site=${encodeURIComponent(brokerSlug)}` : ''}`,
        body,
        { timeout: START_TIMEOUT_MS }
      );
      if (isStartResponse(res.data)) {
        setResult(res.data);
        setStage('done');
      } else {
        setAlreadyDetail((res.data as { detail?: string })?.detail || DUPLICATE_FALLBACK);
        setStage('already');
      }
    } catch (err) {
      if (mayHaveReachedServer(err)) {
        setStage('uncertain');
        return;
      }
      const parsed = getFieldErrors(err);
      // A dotted key ("traveller_ages.0") belongs to the field it starts with.
      const collapsed: Record<string, string> = {};
      for (const [key, message] of Object.entries(parsed)) {
        const root = key === NON_FIELD_KEY ? key : key.split('.')[0];
        if (!(root in collapsed)) collapsed[root] = message;
      }
      const rendered = line === 'health' ? HEALTH_FIELDS : TRAVEL_FIELDS;
      const orphaned = Object.entries(collapsed)
        .filter(([key]) => key !== NON_FIELD_KEY && !rendered.has(key))
        .map(([, message]) => message);
      setFieldErrors(collapsed);
      const banner = [collapsed[NON_FIELD_KEY], ...orphaned].filter(Boolean).join(' ');
      if (banner) setFormError(banner);
      else if (Object.keys(collapsed).length === 0) setFormError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ----------------------------------------------------------- uncertain ---
  // ------------------------------------------------------------- already ---
  // A repeat of a request we already hold, from something other than the
  // attempt that made it: the server says so and hands over nothing that
  // opens it (pre-deploy review, 24 Sep).
  if (stage === 'already') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <Panel tone="neutral" title="We already have your request">
            <p role="status">{alreadyDetail}</p>
          </Panel>
        </div>
      </div>
    );
  }

  if (stage === 'uncertain') {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-900 sm:px-6">
        <div className="mx-auto max-w-2xl">
          <Panel tone="warn" title="Your request may already be with us">
            <p role="status">
              We did not hear back in time, but your request may already have reached us. Please do
              not submit it again -- your advisor will contact you on{' '}
              <span className="font-semibold">{contact.phone}</span>.
            </p>
          </Panel>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- done ---
  // The SAME final page as motor (owner, 24 Sep): `/public/quotes/<token>`,
  // with what the start said drawn above the options (`StartNotes`).
  if (stage === 'done' && result) {
    return <Navigate to={quotePagePath(result.public_token)} replace state={{ start: result }} />;
  }

  // ---------------------------------------------------------------- form ---
  const members = healthMembers();
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-900 sm:px-6">
      <div className="mx-auto max-w-2xl">
        <header className="mb-8">
          <Link
            to={quoteBasePath(brokerSlug)}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            &larr; Motor, Health or Travel
          </Link>
          <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{title}</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {line === 'health'
              ? 'Tell us who to cover. Your advisor prepares quotes from several insurers and they appear on your quote page.'
              : 'Tell us about your trip. Your advisor prepares quotes from several insurers and they appear on your quote page.'}
          </p>
        </header>

        <form
          onSubmit={submit}
          noValidate
          aria-label={title}
          className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
        >
          {formError && (
            <div role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
              {formError}
            </div>
          )}

          {/* Honeypot -- off-screen, not display:none (some bots skip hidden inputs). */}
          <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
            <label htmlFor="company_website">Company website</label>
            <input
              id="company_website"
              name="company_website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
            />
          </div>

          {line === 'health' ? (
            <div className="space-y-5">
              <fieldset>
                <legend className={labelClass}>Who should the plan cover?</legend>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                  {(
                    [
                      ['self', 'Myself'],
                      ['spouse', 'Spouse'],
                      ['father', 'Father'],
                      ['mother', 'Mother'],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key} className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary-600"
                        checked={cover[key]}
                        onChange={(e) => setCover({ ...cover, [key]: e.target.checked })}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="mt-3 max-w-[12rem]">
                  <label htmlFor="children" className={labelClass}>
                    Children
                  </label>
                  <select
                    id="children"
                    className={inputClass}
                    value={childCount}
                    onChange={(e) => setChildCount(Number(e.target.value))}
                  >
                    {[0, 1, 2, 3, 4].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <FieldError message={fieldErrors.members} />
              </fieldset>

              {members.length > 0 && (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {members.map((m) => (
                    <AgeInput
                      key={m.key}
                      id={`age-${m.key}`}
                      label={m.label}
                      value={ages[m.key] ?? ''}
                      onChange={(value) => setAges({ ...ages, [m.key]: value })}
                    />
                  ))}
                </div>
              )}

              <div>
                <label htmlFor="sum_insured" className={labelClass}>
                  Sum insured
                </label>
                <select
                  id="sum_insured"
                  className={inputClass}
                  value={sumInsured}
                  onChange={(e) => setSumInsured(e.target.value)}
                >
                  {SUM_INSURED_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <FieldError message={fieldErrors.sum_insured} />
              </div>

              <fieldset>
                <legend className={labelClass}>Does anyone to be covered have a pre-existing illness?</legend>
                <div className="mt-2 flex gap-5">
                  {(['no', 'yes'] as const).map((value) => (
                    <label key={value} className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                      <input
                        type="radio"
                        name="pre_existing_condition"
                        className="h-4 w-4 accent-primary-600"
                        checked={preExisting === value}
                        onChange={() => setPreExisting(value)}
                      />
                      {value === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div>
                <button
                  type="button"
                  onClick={() => setShowExisting(!showExisting)}
                  aria-expanded={showExisting}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  {showExisting ? 'Hide existing policy' : 'I already have a health policy (optional)'}
                </button>
                {showExisting && (
                  <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="existing_insurer_name" className={labelClass}>
                        Current insurer
                      </label>
                      <input
                        id="existing_insurer_name"
                        className={inputClass}
                        value={existing.insurer}
                        onChange={(e) => setExisting({ ...existing, insurer: e.target.value })}
                      />
                      <FieldError message={fieldErrors.existing_insurer_name} />
                    </div>
                    <div>
                      <label htmlFor="existing_policy_expiry" className={labelClass}>
                        Policy expiry date
                      </label>
                      <input
                        id="existing_policy_expiry"
                        type="date"
                        className={inputClass}
                        value={existing.expiry}
                        onChange={(e) => setExisting({ ...existing, expiry: e.target.value })}
                      />
                      <FieldError message={fieldErrors.existing_policy_expiry} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <fieldset>
                <legend className={labelClass}>Trip type</legend>
                <div className="mt-2 flex flex-wrap gap-5">
                  {(
                    [
                      ['single', 'Single trip'],
                      ['annual_multi_trip', 'Annual multi-trip'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                      <input
                        type="radio"
                        name="trip_type"
                        className="h-4 w-4 accent-primary-600"
                        checked={tripType === value}
                        onChange={() => setTripType(value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="region" className={labelClass}>
                    Destination
                  </label>
                  <select id="region" className={inputClass} value={region} onChange={(e) => setRegion(e.target.value)}>
                    {TRAVEL_REGIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <FieldError message={fieldErrors.region} />
                </div>
                <div>
                  <label htmlFor="countries" className={labelClass}>
                    Countries (optional)
                  </label>
                  <input
                    id="countries"
                    className={inputClass}
                    placeholder="e.g. France, Italy"
                    value={countries}
                    onChange={(e) => setCountries(e.target.value)}
                  />
                  <FieldError message={fieldErrors.countries} />
                </div>
                <div>
                  <label htmlFor="start_date" className={labelClass}>
                    {tripType === 'single' ? 'Departure date' : 'Cover starts'}
                  </label>
                  <input
                    id="start_date"
                    type="date"
                    min={todayIso()}
                    className={inputClass}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                  <FieldError message={fieldErrors.start_date} />
                </div>
                {tripType === 'single' && (
                  <div>
                    <label htmlFor="end_date" className={labelClass}>
                      Return date
                    </label>
                    <input
                      id="end_date"
                      type="date"
                      min={startDate || todayIso()}
                      className={inputClass}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                    />
                    <FieldError message={fieldErrors.end_date} />
                  </div>
                )}
                <div>
                  <label htmlFor="purpose" className={labelClass}>
                    Purpose of travel
                  </label>
                  <select id="purpose" className={inputClass} value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                    {TRAVEL_PURPOSES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="travellers" className={labelClass}>
                    Travellers
                  </label>
                  <select
                    id="travellers"
                    className={inputClass}
                    value={travellerCount}
                    onChange={(e) => setTravellers(Number(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {travellerAges.map((value, index) => (
                  <AgeInput
                    key={index}
                    id={`traveller-age-${index + 1}`}
                    label={`Traveller ${index + 1} age`}
                    value={value}
                    onChange={(next) =>
                      setTravellerAges(travellerAges.map((a, i) => (i === index ? next : a)))
                    }
                  />
                ))}
              </div>
              <FieldError message={fieldErrors.traveller_ages} />
            </div>
          )}

          <hr className="my-6 border-gray-200 dark:border-gray-700" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="name" className={labelClass}>
                Your name
              </label>
              <input
                id="name"
                autoComplete="name"
                className={inputClass}
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                required
              />
              <FieldError message={fieldErrors.name} />
            </div>
            <div>
              <label htmlFor="phone" className={labelClass}>
                Mobile number
              </label>
              <input
                id="phone"
                inputMode="numeric"
                autoComplete="tel"
                className={inputClass}
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                required
              />
              <FieldError message={fieldErrors.phone} />
            </div>
            <div>
              <label htmlFor="pincode" className={labelClass}>
                Pincode
              </label>
              <input
                id="pincode"
                inputMode="numeric"
                autoComplete="postal-code"
                className={inputClass}
                value={contact.pincode}
                onChange={(e) =>
                  setContact({ ...contact, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })
                }
                required
              />
              <FieldError message={fieldErrors.pincode} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="email" className={labelClass}>
                Email (optional)
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={inputClass}
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
              />
              <FieldError message={fieldErrors.email} />
            </div>
          </div>

          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Your details go to the broker whose page you are on, so they can prepare your quotes and
            contact you.
          </p>

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-5 py-3 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {submitting && <LoadingSpinner size="sm" className="text-white dark:text-white" />}
            Get my quotes
          </button>
        </form>
      </div>
    </div>
  );
}
